import React, { useEffect, useState } from "react";
import { X, Clock } from "lucide-react";
import { supabase } from "../../supabase-client";

const PedidoModal = ({ pedido, onClose }) => {
   // 👈 ALL hooks must be at the top, before any conditional logic
   const [historial, setHistorial] = useState([]);
   const [loading, setLoading] = useState(false);

   // 👈 useEffect MUST be before any conditional returns
   useEffect(() => {
      if (pedido?.id) {
         fetchHistorial();
      }
   }, [pedido?.id]);

   // 👈 NOW we can do the early return
   if (!pedido) return null;

   const fetchHistorial = async () => {
      setLoading(true);
      try {
         const { data, error } = await supabase
            .from("pedidos_hist")
            .select("*")
            .eq("pedido_id", pedido.id)
            .order("created_at", { ascending: false });

         if (error) {
            console.error("Error fetching historial:", error);
            return;
         }

         setHistorial(data || []);
      } catch (err) {
         console.error("Error:", err);
      }
      setLoading(false);
   };

   // Format date
   const formatDate = (dateString) => {
      return new Date(dateString).toLocaleString("es-AR", {
         year: "numeric",
         month: "2-digit",
         day: "2-digit",
         hour: "2-digit",
         minute: "2-digit",
      });
   };

   // Parse obras JSON safely
   const parseObras = (obrasJson) => {
      try {
         return JSON.parse(obrasJson || "[]");
      } catch {
         return [];
      }
   };

   // Parse direccion safely
   const parseDireccion = (direccionJson) => {
      try {
         if (typeof direccionJson === "string") {
            const dir = JSON.parse(direccionJson);
            return `${dir.calle}, ${dir.ciudad}, ${dir.provincia} (${dir.codigo_postal})`;
         }
         return "Dirección no disponible";
      } catch {
         return "Dirección no disponible";
      }
   };

   // Format estado for better display
   const formatEstado = (estado) => {
      const estadoMap = {
         pendiente: { label: "Pendiente", color: "text-yellow-600" },
         confirmado: { label: "Confirmado", color: "text-blue-600" },
         pagado: { label: "Pagado", color: "text-green-600" },
         procesando: { label: "Procesando", color: "text-purple-600" },
         enviado: { label: "Enviado", color: "text-orange-600" },
         entregado: { label: "Entregado", color: "text-green-700" },
         cancelado: { label: "Cancelado", color: "text-red-600" },
      };
      
      return estadoMap[estado] || { label: estado, color: "text-gray-600" };
   };
   console.log('pedido: ', pedido);
   
   return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
         <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
               {/* Header */}
               <div className="flex justify-between items-start mb-4">
                  <h2 className="text-2xl font-bold">
                     Detalle del Pedido #{pedido.id}
                  </h2>
                  <button
                     onClick={onClose}
                     className="text-gray-400 hover:text-gray-600 text-2xl transition-colors"
                  >
                     <X className="w-6 h-6" />
                  </button>
               </div>

               {/* Cliente Info */}
               <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold mb-2 text-gray-800">
                     Información del Cliente
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                     <div>
                        <span className="font-medium text-gray-600">Nombre:</span>{" "}
                        <span className="text-gray-900">
                           {pedido.usuarios?.nombre || "N/A"}
                        </span>
                     </div>
                     <div>
                        <span className="font-medium text-gray-600">Email:</span>{" "}
                        <span className="text-gray-900">
                           {pedido.usuarios?.email || "N/A"}
                        </span>
                     </div>
                     <div>
                        <span className="font-medium text-gray-600">Documento:</span>{" "}
                        <span className="text-gray-900">
                           {pedido.usuarios?.documento || "N/A"}
                        </span>
                     </div>
                     <div>
                        <span className="font-medium text-gray-600">Celular:</span>{" "}
                        <span className="text-gray-900">
                           {pedido.usuarios?.celular || "N/A"}
                        </span>
                     </div>
                  </div>
               </div>

               {/* Dirección */}
               {pedido.direccion_entrega && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                     <h3 className="font-semibold mb-2 text-gray-800">
                        Dirección de Entrega
                     </h3>
                     <div className="text-sm text-gray-900">
                        {parseDireccion(pedido.direccion_entrega)}
                     </div>
                  </div>
               )}

               {/* Obras */}
               <div className="mb-6">
                  <h3 className="font-semibold mb-3 text-gray-800">
                     Obras del Pedido ({parseObras(pedido.obras).length}{" "}
                     {parseObras(pedido.obras).length === 1 ? "obra" : "obras"})
                  </h3>
                  <div className="space-y-3">
                     {parseObras(pedido.obras).map((obra, index) => (
                        <div
                           key={index}
                           className="flex justify-between items-start p-4 bg-gray-50 rounded-lg border border-gray-200"
                        >
                           <div className="flex-1">
                              <div className="font-medium text-gray-900 mb-1">
                                 {obra.imagenes && obra.imagenes.length > 0 ? (
                                    <img
                                       src={obra.imagenes[0].imagen_url}
                                       alt={obra.titulo}
                                       className="w-3/12 h-auto rounded"
                                    />
                                 ) : (
                                    <div className="w-3/12 h-48 bg-gray-200 flex items-center justify-center rounded">
                                       <span className="text-gray-500">No Image</span>
                                    </div>
                                 )}
                              </div>
                              <div className="font-medium text-gray-900 mb-1">
                                 {obra.titulo}
                              </div>
                              <div className="text-sm text-gray-600">
                                 <span className="font-medium">Medidas:</span> {obra.medidas}
                              </div>
                              {obra.descripcion && (
                                 <div className="text-sm text-gray-600 mt-1">
                                    <span className="font-medium">Descripción:</span> {obra.descripcion}
                                 </div>
                              )}
                           </div>
                           <div className="ml-4 text-right">
                              <div className="font-bold text-green-600 text-lg">
                                 U$S {Number(obra.precio_usd || 0).toLocaleString()}
                              </div>
                           </div>
                           <div className="ml-4 text-right">
                              <div className="font-bold text-green-600 text-lg">
                                 $ {Number(obra.precio_pesos || 0).toLocaleString()}
                              </div>
                           </div>
                        </div>
                     ))}
                  </div>
               </div>

               {/* Enhanced Historial Section */}
               <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold mb-3 text-gray-800 flex items-center">
                     <Clock className="w-5 h-5 mr-2" />
                     Historial del Pedido
                  </h3>
                  
                  {loading ? (
                     <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent"></div>
                        <span className="ml-2 text-sm text-gray-600">Cargando historial...</span>
                     </div>
                  ) : historial.length > 0 ? (
                     <div className="space-y-2">
                        {historial.map((cambio, index) => {
                           const estadoInfo = formatEstado(cambio.estado);
                           return (
                              <div
                                 key={index}
                                 className="flex justify-between items-center p-3 bg-white rounded border border-gray-200"
                              >
                                 <div className="flex items-center space-x-3">
                                    <div className={`font-medium ${estadoInfo.color}`}>
                                       {estadoInfo.label}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                       {formatDate(cambio.created_at)}
                                    </div>
                                 </div>
                                 {index === 0 && (
                                    <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                                       Actual
                                    </span>
                                 )}
                              </div>
                           );
                        })}
                     </div>
                  ) : (
                     <div className="text-sm text-gray-500 py-2">
                        No hay historial de cambios disponible
                     </div>
                  )}
               </div>

               {/* Payment Info */}
               {pedido.metodo_pago && (
                  <div className="mb-6 p-4 bg-green-50 rounded-lg">
                     <h3 className="font-semibold mb-2 text-gray-800">
                        Información de Pago
                     </h3>
                     <div className="text-sm">
                        <div className="mb-1">
                           <span className="font-medium text-gray-600">Método de pago:</span>{" "}
                           <span className="text-gray-900">{pedido.metodo_pago}</span>
                        </div>
                        {pedido.estado && (
                           <div>
                              <span className="font-medium text-gray-600">Estado:</span>{" "}
                              <span className={`font-medium ${formatEstado(pedido.estado).color}`}>
                                 {formatEstado(pedido.estado).label}
                              </span>
                           </div>
                        )}
                     </div>
                  </div>
               )}

               {/* Summary */}
               <div className="border-t pt-4 bg-yellow-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-3">
                     <span className="text-xl font-bold text-gray-800">Total del Pedido:</span>
                     <span className="text-xl font-bold text-green-600">
                        ${Number(pedido.total || 0).toLocaleString()}
                     </span>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                     <div>
                        <span className="font-medium">Fecha del pedido:</span>{" "}
                        {formatDate(pedido.fecha_pedido)}
                     </div>
                     {pedido.updated_at && pedido.updated_at !== pedido.fecha_pedido && (
                        <div>
                           <span className="font-medium">Última actualización:</span>{" "}
                           {formatDate(pedido.updated_at)}
                        </div>
                     )}
                  </div>
               </div>

               {/* Action Buttons */}
               <div className="mt-6 flex justify-center space-x-3">
                  <button
                     onClick={onClose}
                     className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
                  >
                     Cerrar
                  </button>
               </div>
            </div>
         </div>

         {/* Click outside to close */}
         <div className="absolute inset-0 -z-10" onClick={onClose} />
      </div>
   );
};

export default PedidoModal;